import { useState, useEffect, type ReactNode } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
// useUserBootstrap removed — see Win95Desktop body comment.
import { useFeatureFlags, type PortalFeatureFlags } from "../../lib/tenant/useFeatureFlags";
import { Taskbar, BAR_HEIGHT } from "./Taskbar";
import { StatusBar, STATUS_BAR_HEIGHT } from "./StatusBar";
import { NipeOverlay, FIRST_CLICK_EVENT, type FirstClickEventDetail } from "../nipe/NipeOverlay";
import { NipeControlProvider } from "../nipe/NipeControlContext";
import {
  hasSeenFirstClick,
  markFirstClickSeen,
} from "../nipe/nipeFirstClickCommentary";
import { DesktopIcon } from "./DesktopIcon";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { WindowProvider, useWindowContext } from "./WindowContext";
import { buildDragDropSource } from "../../lib/vault/importers/dragdrop";
import { dispatchPortableSiteDrop } from "../../lib/portable-site/desktopDrop";
// FUTURE-REVIEW: NipePortableSiteDropDialog import + mount disabled.
// To re-enable, restore:
//   import { NipePortableSiteDropDialog } from "../nipe/NipePortableSiteDropDialog";
// and uncomment the <NipePortableSiteDropDialog /> mount below.
import { WindowLayer } from "./WindowLayer";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { ExplorerWindow } from "../sharepoint/ExplorerWindow";
import { PromptLabWindow } from "../prompt-lab/PromptLabWindow";
// FUTURE-REVIEW: ClientListWindow folded into Document Library Common
// Tasks ("Clients" jump-to-root link). Code stays on disk:
//   import { ClientListWindow } from "../clients/ClientListWindow";
import { SettingsWindow } from "../settings/SettingsWindow";
import { HelpWindow } from "../help/HelpWindow";
import { SkillLibraryWindowConnected } from "../skills/SkillLibraryWindowConnected";
import { SpecializationLabWindow } from "../specialization-lab/SpecializationLabWindow";
import { CorpusSearchWindow } from "../corpus/CorpusSearchWindow";
import { ObsidianImportWindowHost } from "../obsidian-import/ObsidianImportWindowHost";
import { ObsidianVaultWindow } from "../obsidian-vault/ObsidianVaultWindow";
import { OnboardingWizardWindow } from "../onboard/OnboardingWizardWindow";
// FUTURE-REVIEW: Strong deprecation pass — these standalone Win95
// windows are no longer registered. Code stays on disk; functionality:
//   - VaultGraphWindow / VaultFlowWindow / BasesWindow → folded into
//     ObsidianVaultWindow as panes (graph pane is already embedded).
//   - WizardShell ("New Portal") + SchemaProfilesWindow → operator-
//     only routes, no lawyer-facing desktop window.
//   - WorkspaceSetupPanel → reached via Settings tab / NIPE / direct
//     URL `/admin/workspace-setup` (still has a route).
// Client List stays registered (reachable from Start menu only) per
// user direction — desktop icon retired, window retained.
// import { VaultGraphWindow } from "../obsidian-vault/VaultGraphWindow";
// import { VaultFlowWindow } from "../obsidian-vault/VaultFlowWindow";
// import { BasesWindow } from "../bases/BasesWindow";
// import { WizardShell } from "../portal-wizard/WizardShell";
// import { SchemaProfilesWindow } from "../admin/SchemaProfilesWindow";
// import { WorkspaceSetupPanel } from "../onboarding/PortalProvisioningInterstitial";
// Phase F stuck-recovery bubbles intentionally retired in the XP refresh
// — they sat detached above the taskbar and were rarely actionable. The
// NipeStuckBubbles component + useStuckDetection hook are kept on disk
// for other surfaces that may want to opt back in.
import { TestWindowBridge } from "../../lib/test-hooks/windowBridge";
import { ConflictDialogTestMount } from "../../lib/test-hooks/conflictDialogBridge";
import { testIds } from "../../lib/testIds";
import { FirstRunTour } from "../tour/FirstRunTour";
import { useFirstRunTour } from "../../lib/tour/useFirstRunTour";

interface Win95DesktopProps {
  children: ReactNode;
}

interface DesktopIconDef {
  label: string;
  path: string;
  /** C.2 — when set, the icon only renders if the portal flag is on. */
  requiresFlag?: keyof PortalFeatureFlags;
}

// Phase D / Q4 / J.3 — every signed-in user is fully empowered.
//
// All previous role-based gating (`requiresRole: 'operator'`) was removed.
// J.3 (2026-05-20) reinforces this: per the locked user direction "all
// users should be able to create their own portal", the Convex
// `portalProvisioning.*` surfaces use `requireIdentity` instead of
// `requireOperator`. Schema Profiles still has its operator gate
// server-side (it's a genuinely operator-only surface) but the icon
// remains visible to all so the operator can see what exists.
//
// XP.2 — desktop icons resolve their PNG via `getXpIcon(path)` inside
// DesktopIcon; the per-row icon string is gone.
//
// Desktop is the lawyer-first surface. Power-user, admin, and Notes
// (Obsidian) tooling moved into the Start menu submenus. Routes + window
// registrations stay live so direct URLs and NIPE/Start-menu callers
// still work.
const desktopIcons: ReadonlyArray<DesktopIconDef> = [
  { label: "Document Library (SharePoint)", path: "/documents" },
  { label: "Prompt Lab", path: "/prompt-lab" },
  { label: "Skill Library", path: "/skills" },
  { label: "Specialization Lab", path: "/specialization-lab" },
  { label: "Corpus Search", path: "/corpus-search" },
  { label: "Settings", path: "/settings" },
  { label: "Help", path: "/help" },
  // F18 — customer-facing portal provisioning wizard.
  { label: "Set up SharePoint", path: "/onboard/new" },
  // 2026-05-27 — Obsidian Import desktop affordance. Window + icon
  // mapping already registered; this row makes it a first-class desktop
  // entry alongside the existing Start menu Notes submenu item.
  // 2026-05-27 — single Obsidian entry. Opens the vault explorer
  // (preloaded with the morrison-legal sample vault); the explorer
  // itself has an "Import vault" button that fires /obsidian-import
  // for drag/folder/zip/SharePoint pulls. Two icons was redundant.
  { label: "Obsidian Explorer", path: "/obsidian-vault" },
];

interface CtxState {
  x: number;
  y: number;
  items: ContextMenuItem[];
}

function DesktopInner({ children }: { children: ReactNode }) {
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [ctx, setCtx] = useState<CtxState | null>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const iconScale = isMobile ? 0.9 : 1;
  const { openWindow, focusWindow, getWindow, registerWindow } = useWindowContext();
  // Phase D — every signed-in user sees every icon (Q4). The previous
  // role-gating was deleted; only the per-portal feature-flag gate
  // remains. Demo portals default flags ON; live portals OFF until an
  // operator enables them.
  const flags = useFeatureFlags();
  const visibleIcons = desktopIcons.filter((icon) => {
    if (icon.requiresFlag !== undefined && !flags[icon.requiresFlag]) return false;
    return true;
  });

  useEffect(() => {
    // STARTUP UX: only one window auto-opens (Document Library — the
    // "this is your real SharePoint" payoff). Every other window is
    // registered closed; users open them on demand. Pre-trim, 12+
    // windows mounted at sign-in which is overwhelming for a novel
    // system.
    registerWindow("/documents", "Document Library — SharePoint", <ExplorerWindow />, { width: 1035, height: 620, minWidth: 640, minHeight: 420, centered: true, startMaximized: false });
    registerWindow("/prompt-lab", "Prompt Lab", <PromptLabWindow />, { width: 850, height: 640, minWidth: 640, minHeight: 420, centered: true, startClosed: true, offsetXPercent: 0.02, offsetYPercent: 0.02 });
    // Client List window deregistered — Document Library's Common
    // Tasks pane already exposes "Clients" as a TreePane jump-to-root
    // link. ClientListWindow code stays on disk under
    // components/clients/. Start-menu entry now points to /documents.
    registerWindow("/skills", "Skill Library", <SkillLibraryWindowConnected />, { width: 760, height: 600, minWidth: 560, minHeight: 400, centered: true, startClosed: true, offsetXPercent: 0.04, offsetYPercent: 0.04 });
    registerWindow("/corpus-search", "Corpus Search", <CorpusSearchWindow />, { width: 720, height: 600, minWidth: 560, minHeight: 400, centered: true, startClosed: true, offsetXPercent: -0.04, offsetYPercent: -0.02 });
    // L.7 — Specialization Lab. Opens via desktop icon, Start menu, or
    // automatically by TrainLoraButton on training-complete (L.10).
    registerWindow("/specialization-lab", "Specialization Lab", <SpecializationLabWindow />, { width: 940, height: 640, minWidth: 720, minHeight: 480, centered: true, startClosed: true, offsetXPercent: 0.02, offsetYPercent: -0.02, contentOverflowY: "hidden" });
    registerWindow("/settings", "Settings", <SettingsWindow />, { width: 700, height: 560, minWidth: 560, minHeight: 400, centered: true, startClosed: true });
    registerWindow("/help", "Help", <HelpWindow />, { width: 580, height: 520, minWidth: 420, minHeight: 360, centered: true, startClosed: true });
    // Obsidian Import — registered unconditionally; the desktop-icon
    // visibility (Start menu Notes submenu) is the flag gate.
    registerWindow("/obsidian-import", "Obsidian Import", <ObsidianImportWindowHost />, { width: 720, height: 580, minWidth: 560, minHeight: 400, centered: true, startClosed: true, offsetXPercent: 0.06, offsetYPercent: 0.02 });
    // Obsidian Vault — unified "Vault Manager". Graph pane is already
    // embedded (VaultGraphPane); the previous standalone VaultGraph /
    // VaultFlow / Bases windows are deprecated and code stays on disk
    // (they may return as additional panes inside this window).
    registerWindow("/obsidian-vault", "Obsidian Vault", <ObsidianVaultWindow />, { width: 1180, height: 760, minWidth: 720, minHeight: 480, centered: true, startClosed: true, startMaximized: false, contentOverflowY: "hidden" });
    // F18 — customer-facing portal provisioning wizard.
    registerWindow("/onboard/new", "Set up SharePoint", <OnboardingWizardWindow />, { width: 640, height: 600, minWidth: 480, minHeight: 420, centered: true, startClosed: true, contentOverflowY: "auto" });
  }, []);

  // Auto-open the F18 wizard when first-time signers land here via
  // the orientation redirect (which __root.tsx queues through the
  // attention orchestrator on first signed-in render). The redirect
  // sets the orientation flag + navigates here with ?onboard=auto;
  // the desktop renders normally and we open the wizard as a window
  // on top so the user sees the product behind the wizard.
  //
  // The query param is consumed once + stripped from the URL so a
  // hard-reload doesn't re-open the wizard if the user closed it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('onboard') !== 'auto') return;
    // Scrub the param. replaceState avoids a navigation + preserves
    // the rest of the URL state. Wrap so SSR / non-history browsers
    // can't throw.
    try {
      params.delete('onboard');
      const rest = params.toString();
      const newPath =
        window.location.pathname + (rest.length > 0 ? `?${rest}` : '');
      window.history.replaceState({}, '', newPath);
    } catch {
      /* non-fatal */
    }
    // openWindow is stable across re-renders (provided by the
    // useWindowContext singleton); registerWindow inside the first
    // effect runs synchronously before this effect, so /onboard/new
    // is guaranteed to be registered by the time we try to open it.
    openWindow('/onboard/new');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleItemClick(path: string) {
    // Per-icon first-click NIPE bubbles were removed 2026-05-27 —
    // they duplicated the FirstRunTour bubble on top of the tour
    // itself. Tour is the single source of truth for "here's what
    // this icon does" copy. The portal-welcome sticky balloon (see
    // the isCustomerProvisioned effect below) still uses the same
    // FIRST_CLICK_EVENT pipe for its own purpose.
    const win = getWindow(path);
    if (win) {
      if (win.state === "closed" || win.state === "minimized") {
        openWindow(path);
      } else {
        focusWindow(path);
      }
    } else {
      openWindow(path);
    }
  }

  function handleDesktopRightClick(e: React.MouseEvent) {
    e.preventDefault();
    setCtx({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: "Arrange Icons", onClick: () => {}, disabled: true },
        { label: "Refresh", onClick: () => window.location.reload() },
        { divider: true, label: "", onClick: () => {} },
        { label: "Properties", onClick: () => {}, disabled: true },
      ],
    });
  }

  // Expose taskbar height as a CSS custom property so overlays (NIPE
  // sprite, balloon) can stack above the taskbar without hardcoded values.
  useEffect(() => {
    document.documentElement.style.setProperty('--taskbar-height', `${BAR_HEIGHT}px`);
    // Phase E — status bar sits above the taskbar; expose its height so
    // overlays / windows can offset above it when needed.
    document.documentElement.style.setProperty('--statusbar-height', `${STATUS_BAR_HEIGHT}px`);
  }, []);

  // F18.G.1 — Customer-provisioned portal detection. Used to (a) flip
  // the desktop wallpaper to a forest-green gradient via the
  // `data-portal-provisioned` attribute, and (b) fire a one-time NIPE
  // welcome message — both as a sticky balloon AND as a persistent
  // assistant message in the NIPE chat history, so when the user opens
  // the chat panel the welcome is sitting there waiting for them.
  const healthSummary = useQuery(api.health.summary);
  const currentSlug = healthSummary?.currentUserPortalSlug ?? null;
  const isCustomerProvisioned = useQuery(
    api.portalProvisioning.isCustomerProvisioned,
    currentSlug ? { clientSlug: currentSlug } : 'skip',
  );
  const getOrCreateChat = useMutation(api.nipeChats.getOrCreateForPortal);
  const appendNipeMessage = useMutation(api.nipeMessages.appendMessage);

  useEffect(() => {
    if (isCustomerProvisioned !== true) return;
    if (!currentSlug) return;
    const welcomeKey = `welcome:onboarding:${currentSlug}`;
    if (hasSeenFirstClick(welcomeKey)) return;
    markFirstClickSeen(welcomeKey);

    const welcomeText =
      `Welcome to "${currentSlug}". This portal is yours — everything you ` +
      `do here lives in your own SharePoint tenant, not in a Repancy demo ` +
      `sandbox. A few things to notice:\n\n` +
      `• The forest-green wallpaper is the visual cue that you're in your ` +
      `own portal. Demo portals stay Luna blue.\n` +
      `• Your Document Library mirrors the SharePoint site we just ` +
      `provisioned for you. Any saves go back to that tenant.\n` +
      `• The Skill Library is empty by default — drop in SKILL.md files ` +
      `or specialize the base model on whatever you upload.\n\n` +
      `Click the × on this balloon when you're ready to get started, or ` +
      `open me up (the chat panel) and I'll answer questions about any of ` +
      `it.`;

    // Sticky balloon — stays until user dismisses with ×.
    document.dispatchEvent(
      new CustomEvent<FirstClickEventDetail>(FIRST_CLICK_EVENT, {
        detail: { text: welcomeText, sticky: true },
      }),
    );

    // Also persist into the NIPE chat history so the message survives
    // balloon dismissal and shows up when the user later opens the chat.
    // Failures here are non-fatal — the balloon already delivered the
    // welcome and localStorage gates re-fire either way.
    void (async () => {
      try {
        const chatId = await getOrCreateChat({
          clientSlug: currentSlug,
          modelId: 'anthropic/claude-sonnet-4.6',
          route: 'convex-backup',
        });
        await appendNipeMessage({
          chatId: String(chatId),
          clientSlug: currentSlug,
          role: 'assistant',
          content: welcomeText,
        });
      } catch {
        /* non-fatal — see above */
      }
    })();
  }, [isCustomerProvisioned, currentSlug, getOrCreateChat, appendNipeMessage]);

  return (
    <div
      onClick={() => setSelectedIcon(null)}
      onContextMenu={handleDesktopRightClick}
      onDragOver={(e) => {
        // Allow folder drops on the desktop root. Without preventDefault
        // the browser ignores the drop target.
        if (e.dataTransfer?.types.includes('Files')) {
          e.preventDefault();
        }
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.types.includes('Files')) return;
        e.preventDefault();
        const source = buildDragDropSource(e.nativeEvent as DragEvent);
        if (source) {
          dispatchPortableSiteDrop({ source, origin: 'desktop' });
        }
      }}
      data-testid={testIds.desktop.root}
      data-portal-provisioned={
        isCustomerProvisioned === true ? 'onboarding' : undefined
      }
      className="xp-desktop fixed inset-0 overflow-hidden"
    >
      {/* Desktop icons */}
      <div
        style={isMobile ? {
          position: "absolute",
          top: 12,
          left: 0,
          right: 0,
          bottom: BAR_HEIGHT,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 4,
          padding: "0 8px",
          alignContent: "start",
          justifyItems: "center",
          overflow: "auto",
          zIndex: 10,
        } : {
          position: "absolute",
          top: 16,
          left: 16,
          display: "grid",
          gridTemplateRows: "repeat(4, auto)",
          gridAutoFlow: "column",
          gap: 8,
          columnGap: 12,
          zIndex: 10,
        }}
        data-testid={testIds.desktop.iconGrid}
      >
        {visibleIcons.map((icon) => (
          <DesktopIcon
            key={icon.path}
            label={icon.label}
            path={icon.path}
            selected={selectedIcon === icon.path}
            onSelect={() => setSelectedIcon(icon.path)}
            scale={iconScale}
            onContextMenu={(e, path) => {
              e.preventDefault();
              e.stopPropagation();
              setCtx({
                x: e.clientX,
                y: e.clientY,
                items: [
                  {
                    label: "Open",
                    onClick: () => handleItemClick(path),
                  },
                  { divider: true, label: "", onClick: () => {} },
                  { label: "Create Shortcut", onClick: () => {}, disabled: true },
                  { label: "Properties", onClick: () => {}, disabled: true },
                ],
              });
            }}
          />
        ))}
      </div>

      {/* Outlet runs routes (PageWindow registers windows + caches content) */}
      <div style={{ display: "none" }}>{children}</div>

      {/* All floating windows rendered here */}
      <WindowLayer />

      {/* FUTURE-REVIEW: NipePortableSiteDropDialog disabled. The
          3-way vault/SP/both prompt was rarely used and confused
          non-technical lawyers. Kept on disk; re-evaluate either
          deleting outright or replacing with a default-to-vault drop
          handler. */}
      {/* <NipePortableSiteDropDialog /> */}

      {/* C.10 — NIPE sprite overlay. Sibling of WindowLayer so it stacks
          above every window. Gated by portalFeatureFlags.nipeEnabled. */}
      <NipeOverlay />

      {/* Phase 6 — First-run guided spotlight tour. Auto-fires for
          signed-in users with zero portals AND for signed-out
          showcase visitors. Start Menu "Take the tour" item re-fires
          via the `repancy:tour-relaunch` custom event. */}
      <FirstRunTourMount />

      {/* Phase E — Win95 status bar sits between WindowLayer and Taskbar.
          5 cells: Convex / M365 / Portal / Index / User (Q16). */}
      <StatusBar />

      <Taskbar />

      {/* F-WINDOW-STACK-TEST-HOOK — bridge WindowContext onto
          window.__repancyTestWindow so Phase J specs can openWindow /
          closeAll without dblclicking through overlapping windows.
          Self-gated on ?test=1. */}
      <TestWindowBridge />

      {/* Phase J ConflictDialog test-mount — listens for the
          `repancy:test-force-conflict-dialog` event dispatched by
          `window.__repancyTestForceConflictDialog`. Self-gated on
          ?test=1. Production conflict surface ships via E.4. */}
      <ConflictDialogTestMount />

      {ctx && (
        <ContextMenu
          x={ctx.x}
          y={ctx.y}
          items={ctx.items}
          onClose={() => setCtx(null)}
        />
      )}
    </div>
  );
}

/**
 * Phase 6 — listens to `repancy:tour-relaunch` so the Start Menu
 * "Take the tour" item can fire the tour without remounting the
 * whole desktop. Also runs the legacy-flag migration on mount.
 */
export const TOUR_RELAUNCH_EVENT = 'repancy:tour-relaunch';

function FirstRunTourMount() {
  const tour = useFirstRunTour();
  useEffect(() => {
    function onRelaunch() {
      tour.forceShow();
    }
    window.addEventListener(TOUR_RELAUNCH_EVENT, onRelaunch);
    return () => window.removeEventListener(TOUR_RELAUNCH_EVENT, onRelaunch);
  }, [tour]);
  if (!tour.shouldShow) return null;
  return (
    <FirstRunTour
      mode={tour.mode}
      onDismiss={tour.markDismissed}
      onComplete={tour.markComplete}
    />
  );
}

export function Win95Desktop({ children }: Win95DesktopProps) {
  // Post-D2: useUserBootstrap removed. Users row sync is now handled
  // by the Clerk → /clerk-users-webhook handler in convex/http.ts.
  // App-gating on users-row existence handled by src/lib/useCurrentUser
  // at the root sign-in gate. No auto-navigation after sign-in — the
  // user lands here on `/` and uses Start menu / desktop icons to
  // navigate. Per user direction 2026-05-21.
  return (
    <NipeControlProvider>
      <WindowProvider>
        <DesktopInner>{children}</DesktopInner>
      </WindowProvider>
    </NipeControlProvider>
  );
}
